import React, { useState, useEffect, useRef } from 'react';
import { auth } from './firebase/config';
import { signOut } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Search, Layout, Book, CreditCard } from 'lucide-react';
import { Button } from './components/ui/button';
import { API_URL } from './config';
import { useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import { Tabs, TabsList, TabsTrigger } from './components/ui/tabs';
import { cn } from './lib/utils';
import { useDebounce } from 'use-debounce';
import LogoutButton from './components/LogoutButton';

const SearchPage = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const scrollContainerRef = useRef(null);

    // Get cached values
    const cachedData = queryClient.getQueryData(['lastSearchState']) || {};
    const [searchQuery, setSearchQuery] = useState(cachedData.query || '');
    const [startIndex, setStartIndex] = useState(cachedData.startIndex || 0);
    const [searchResults, setSearchResults] = useState(cachedData.results || []);
    const [scrollPosition, setScrollPosition] = useState(cachedData.scrollPosition || 0);

    const [searchMode, setSearchMode] = useState('search'); // 'search' or 'link'
    const [arxivLink, setArxivLink] = useState('');
    const [addingToLibrary, setAddingToLibrary] = useState({});

    const [debouncedSearchQuery] = useDebounce(searchQuery, 1000); // 1 second delay

    useEffect(() => {
        queryClient.setQueryData(['lastSearchState'], {
            query: searchQuery,
            startIndex,
            results: searchResults,
            scrollPosition
        });
    }, [queryClient, searchQuery, startIndex, searchResults, scrollPosition]);

    // Search query with React Query
    const { data: newPapers = [], isLoading, isFetching } = useQuery({
        queryKey: ['arxivSearch', debouncedSearchQuery, startIndex],
        queryFn: async () => {
            if (!debouncedSearchQuery.trim()) return [];

            const response = await fetch(
                `https://export.arxiv.org/api/query?search_query=all:${encodeURIComponent(debouncedSearchQuery)}&start=${startIndex}&max_results=10`
            );
            const xmlText = await response.text();
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
            const entries = xmlDoc.getElementsByTagName('entry');

            return Array.from(entries).map(entry => ({
                id: entry.getElementsByTagName('id')[0]?.textContent,
                title: entry.getElementsByTagName('title')[0]?.textContent?.trim(),
                summary: entry.getElementsByTagName('summary')[0]?.textContent?.trim(),
                authors: Array.from(entry.getElementsByTagName('author')).map(
                    author => author.getElementsByTagName('name')[0]?.textContent
                ),
                published: entry.getElementsByTagName('published')[0]?.textContent,
                pdfLink: entry.getElementsByTagName('link')[1]?.getAttribute('href')
            }));
        },
        keepPreviousData: true,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
        retry: false,
    });

    // Maintain all loaded papers in state
    const [directPaperResult, setDirectPaperResult] = useState(null);

    useEffect(() => {
        if (newPapers?.length) {
            if (startIndex === 0) {
                setSearchResults(newPapers);
            } else {
                setSearchResults(prev => [...prev, ...newPapers]);
            }
        }
    }, [newPapers, startIndex]);

    useEffect(() => {
        if (searchMode === 'link') {
            setSearchResults([]);
            setDirectPaperResult(null);
        }
    }, [searchMode]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        setStartIndex(0);
    };

    const handleAddToLibrary = async (paper) => {
        try {
            setAddingToLibrary(prev => ({ ...prev, [paper.id]: true }));
            const userId = auth.currentUser?.uid;
            if (!userId) {
                console.error('No user ID found');
                return;
            }

            const match = paper.id.match(/arxiv\.org\/abs\/([0-9]+\.[0-9]+)/);
            if (!match) {
                toast.error(
                    'Could not process paper ID. Please try using the paper\'s arXiv URL instead.',
                    {
                        duration: 3000,
                        position: 'bottom-right',
                        style: {
                            background: '#1a1a1a',
                            color: '#fff',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                        },
                    }
                );
                return;
            }

            const paperId = match[1];
            const paperData = {
                arxivId: paperId,
                arxivUrl: paper.id,
                title: paper.title,
                authors: paper.authors,
                summary: paper.summary,
                published: paper.published,
                pdfLink: paper.pdfLink
            };

            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${API_URL}/papers/${paperId}/library`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(paperData)
            });

            const data = await response.json();

            if (response.status === 400 && data.detail === "You have reached your limit for this plan") {
                toast.error('Upgrade to continue adding papers', {
                    duration: 3000,
                    position: 'bottom-right',
                    style: {
                        background: '#1a1a1a',
                        color: '#fff',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                    },
                });
                navigate('/subscription');
                return;
            }

            if (response.status === 200 && data.success === false && data.message === "No library found") {
                toast.error('Please create a library first', {
                    duration: 3000,
                    position: 'bottom-right',
                    style: {
                        background: '#1a1a1a',
                        color: '#fff',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                    },
                });
                navigate('/library');  // Redirect to library page
                return;
            }

            if (data.message === "Paper already in library") {
                toast.success('Paper already present in library', {
                    duration: 2000,
                    position: 'bottom-right',
                    style: {
                        background: '#1a1a1a',
                        color: '#fff',
                        border: '1px solid rgba(139, 92, 246, 0.3)',
                    },
                });
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to add paper to library');
            }

            // Success case
            toast.success('Added to library!', {
                duration: 2000,
                position: 'bottom-right',
                style: {
                    background: '#1a1a1a',
                    color: '#fff',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                },
            });

            queryClient.invalidateQueries(['libraryPapers']);
        } catch (error) {
            console.error('Error adding paper to library:', error);
            toast.error('Failed to add to library', {
                duration: 2000,
                position: 'bottom-right',
                style: {
                    background: '#1a1a1a',
                    color: '#fff',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                },
            });
        } finally {
            setAddingToLibrary(prev => ({ ...prev, [paper.id]: false }));
        }
    };

    const NavItem = ({ icon, label, path, active }) => (
        <button
            onClick={() => navigate(path)}
            className={`p-3 rounded-xl transition-colors ${active ? 'bg-purple-600/20 text-purple-400' : 'text-gray-400 hover:bg-purple-600/10'
                }`}
            title={label}
        >
            {icon}
        </button>
    );

    // Add this useEffect to handle scroll position
    useEffect(() => {
        if (!isFetching && startIndex > 0 && scrollPosition > 0) {
            const timeoutId = setTimeout(() => {
                requestAnimationFrame(() => {
                    if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTo({
                            top: scrollPosition,
                            behavior: 'instant'
                        });
                    }
                });
            }, 0);
            return () => clearTimeout(timeoutId);
        }
    }, [isFetching, startIndex, scrollPosition, searchResults.length]);

    const loadMore = () => {
        if (scrollContainerRef.current) {
            setScrollPosition(scrollContainerRef.current.scrollTop);
            setStartIndex(prev => prev + 10);
        }
    };


    const handleArxivLink = async (e) => {
        e.preventDefault();
        if (!arxivLink.trim()) return;

        try {
            // Extract paper ID from various arXiv URL formats
            const urlPatterns = [
                /arxiv\.org\/abs\/(\d+\.\d+)/,
                /arxiv\.org\/pdf\/(\d+\.\d+)/,
                /(\d+\.\d+)/
            ];

            let paperId = null;
            for (const pattern of urlPatterns) {
                const match = arxivLink.match(pattern);
                if (match) {
                    paperId = match[1];
                    break;
                }
            }

            if (!paperId) {
                toast.error(
                    'Could not find paper ID in the URL. Please use the paper\'s arXiv URL (e.g., https://arxiv.org/abs/2304.12210)',
                    {
                        duration: 4000,
                        position: 'bottom-right',
                        style: {
                            background: '#1a1a1a',
                            color: '#fff',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                        },
                    }
                );
                return;
            }

            // Fetch paper details from arXiv API
            const response = await fetch(
                `https://export.arxiv.org/api/query?id_list=${paperId}`
            );
            const xmlText = await response.text();

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
            const entry = xmlDoc.getElementsByTagName('entry')[0];

            if (!entry) {
                toast.error('Paper not found');
                return;
            }

            const paper = {
                id: entry.getElementsByTagName('id')[0]?.textContent,
                title: entry.getElementsByTagName('title')[0]?.textContent?.trim(),
                summary: entry.getElementsByTagName('summary')[0]?.textContent?.trim(),
                authors: Array.from(entry.getElementsByTagName('author')).map(
                    author => author.getElementsByTagName('name')[0]?.textContent
                ),
                published: entry.getElementsByTagName('published')[0]?.textContent,
                pdfLink: entry.getElementsByTagName('link')[1]?.getAttribute('href')
            };

            setDirectPaperResult(paper);
            setArxivLink('');
        } catch (error) {
            console.error('Error fetching paper:', error);
            toast.error('Failed to fetch paper details');
        }
    };

    // Add effect to handle tab changes
    useEffect(() => {
        if (searchMode === 'search') {
            setDirectPaperResult(null);
        } else {
            setSearchQuery('');
        }
    }, [searchMode]);

    useEffect(() => {
        if (scrollContainerRef.current && scrollPosition > 0) {
            requestAnimationFrame(() => {
                scrollContainerRef.current?.scrollTo({
                    top: scrollPosition,
                    behavior: 'instant'
                });
            });
        }
    }, [scrollPosition]);

    return (
        <div className="flex h-screen bg-black">
            <Toaster />
            {/* Navigation Sidebar */}
            <div className="w-16 border-r border-purple-900/30 flex flex-col items-center py-4">
                <div className="flex-1 flex flex-col items-center space-y-6">
                    <NavItem
                        icon={<Search />}
                        label="Search"
                        path="/search"
                        active={location.pathname === '/search'}
                    />
                    <NavItem
                        icon={<Layout />}
                        label="Workspace"
                        path="/workspace"
                        active={location.pathname === '/workspace'}
                    />
                    <NavItem
                        icon={<Book />}
                        label="Library"
                        path="/library"
                        active={location.pathname === '/library'}
                    />
                    <NavItem
                        icon={<CreditCard />}
                        label="Subscription"
                        path="/subscription"
                        active={location.pathname === '/subscription'}
                    />
                </div>
                <LogoutButton />
            </div>

            {/* Main Content */}
            <div
                ref={scrollContainerRef}
                className="flex-1 overflow-auto p-8"
            >
                {/* Search/Paste Link Section */}
                <div className="max-w-2xl mx-auto mb-8">
                    <Tabs value={searchMode} onValueChange={setSearchMode} className="mb-4">
                        <TabsList className="grid w-full grid-cols-2 p-1 bg-black/50 border border-purple-900/30 rounded-lg h-[45px] relative">
                            <div
                                className="absolute inset-1 z-0 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-md transition-transform duration-200 ease-out"
                                style={{
                                    transform: `translateX(${searchMode === 'search' ? '0%' : '100%'})`,
                                    width: 'calc(50% - 4px)',
                                }}
                            />
                            <TabsTrigger
                                value="search"
                                className={cn(
                                    "relative z-10",
                                    "data-[state=active]:text-purple-400",
                                    "rounded-md text-sm font-medium",
                                    "text-gray-400 hover:text-purple-300",
                                    "focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:ring-offset-0",
                                    "flex items-center justify-center",
                                    "transition-colors duration-200"
                                )}
                            >
                                <Search className="w-4 h-4 mr-2" />
                                Search Papers
                            </TabsTrigger>
                            <TabsTrigger
                                value="link"
                                className={cn(
                                    "relative z-10",
                                    "data-[state=active]:text-purple-400",
                                    "rounded-md text-sm font-medium",
                                    "text-gray-400 hover:text-purple-300",
                                    "focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:ring-offset-0",
                                    "flex items-center justify-center",
                                    "transition-colors duration-200"
                                )}
                            >
                                <Book className="w-4 h-4 mr-2" />
                                Paste arXiv Link
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {searchMode === 'search' ? (
                        <form onSubmit={handleSearch} className="relative flex items-center">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search arXiv papers..."
                                className="w-full px-4 py-2.5 bg-black/50 border border-purple-900/30 rounded-lg 
                                    text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50
                                    pr-24"
                            />
                            <Button
                                type="submit"
                                disabled={isLoading}
                                className="absolute right-1.5 px-4 h-[calc(100%-0.75rem)] bg-gradient-to-r from-purple-600 to-blue-600 
                                    hover:from-purple-700 hover:to-blue-700 transition-all duration-200 
                                    flex items-center gap-2 text-sm font-medium rounded-md"
                            >
                                {isLoading ? (
                                    "Searching..."
                                ) : (
                                    <>
                                        <Search className="w-4 h-4" />
                                        <span>Search</span>
                                    </>
                                )}
                            </Button>
                        </form>
                    ) : (
                        <form onSubmit={handleArxivLink} className="relative flex items-center">
                            <input
                                type="text"
                                value={arxivLink}
                                onChange={(e) => setArxivLink(e.target.value)}
                                placeholder="Paste arXiv link (e.g., https://arxiv.org/abs/2304.12210)"
                                className="w-full px-4 py-2.5 bg-black/50 border border-purple-900/30 rounded-lg 
                                    text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50
                                    pr-24"
                            />
                            <Button
                                type="submit"
                                disabled={isLoading}
                                className="absolute right-1.5 px-4 h-[calc(100%-0.75rem)] bg-gradient-to-r from-purple-600 to-blue-600 
                                    hover:from-purple-700 hover:to-blue-700 transition-all duration-200 
                                    flex items-center gap-2 text-sm font-medium rounded-md"
                            >
                                Fetch Paper
                            </Button>
                        </form>
                    )}
                </div>

                {/* Search Results */}
                <div className="max-w-4xl mx-auto space-y-6">
                    {searchMode === 'search' ? (
                        // Search results view
                        isLoading && startIndex === 0 ? (
                            <div className="text-center text-gray-400">Loading...</div>
                        ) : (
                            <>
                                {searchResults.map((paper, index) => (
                                    <div key={`${paper.id}-${index}`} className="p-6 bg-black/50 border border-purple-900/30 rounded-lg hover:border-purple-500/50 transition-colors">
                                        <h3 className="text-lg font-semibold text-white mb-2">{paper.title}</h3>
                                        <p className="text-sm text-gray-400 mb-4">
                                            {paper.authors.join(', ')} • {new Date(paper.published).toLocaleDateString()}
                                        </p>
                                        <p className="text-gray-300 mb-4 line-clamp-3">{paper.summary}</p>
                                        <div className="flex justify-between items-center">
                                            <div className="flex gap-2">
                                                <a
                                                    href={paper.pdfLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-purple-400 hover:text-purple-300 transition-colors"
                                                >
                                                    View PDF
                                                </a>
                                            </div>
                                            <Button
                                                onClick={() => handleAddToLibrary(paper)}
                                                className="px-4 py-2 bg-purple-600/20 text-purple-400 
                                                    hover:bg-purple-600/30 transition-colors rounded-md"
                                                disabled={addingToLibrary[paper.id]}
                                            >
                                                {addingToLibrary[paper.id] ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                                        <span>Adding...</span>
                                                    </div>
                                                ) : (
                                                    "Add to Library"
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                {searchResults.length > 0 && (
                                    <div className="flex justify-center mt-6">
                                        <Button
                                            onClick={loadMore}
                                            disabled={isFetching}
                                            className="px-6 py-2 bg-purple-600/20 text-purple-400 
                                                hover:bg-purple-600/30 transition-colors rounded-md"
                                        >
                                            {isFetching ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                                    <span>Loading more...</span>
                                                </div>
                                            ) : (
                                                "Load More"
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </>
                        )
                    ) : (
                        // Direct paper result view
                        directPaperResult && (
                            <div className="p-6 bg-black/50 border border-purple-900/30 rounded-lg hover:border-purple-500/50 transition-colors">
                                <h3 className="text-lg font-semibold text-white mb-2">{directPaperResult.title}</h3>
                                <p className="text-sm text-gray-400 mb-4">
                                    {directPaperResult.authors.join(', ')} • {new Date(directPaperResult.published).toLocaleDateString()}
                                </p>
                                <p className="text-gray-300 mb-4 line-clamp-3">{directPaperResult.summary}</p>
                                <div className="flex justify-between items-center">
                                    <div className="flex gap-2">
                                        <a
                                            href={directPaperResult.pdfLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-purple-400 hover:text-purple-300 transition-colors"
                                        >
                                            View PDF
                                        </a>
                                    </div>
                                    <Button
                                        onClick={() => handleAddToLibrary(directPaperResult)}
                                        className="px-4 py-2 bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 transition-colors rounded-md"
                                    >
                                        Add to Library
                                    </Button>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchPage;